import { AIVideo } from 'almanac-ui/icons/16/AIVideo'
import { VisualStyle } from 'almanac-ui/icons/16/VisualStyle'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { VideoOn } from 'blaze-ui/icons/20/VideoOn'
import { Image03 } from 'blaze-ui/icons/24/Image03'
import { Text } from 'blaze-ui/Text/Text'
import { SourceMediaInfo } from 'hooks/queries/files/types'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

import { ToolbarMenu } from '../Toolbar/ToolbarMenu'
import Styles from './AboutPost.module.scss'

interface AboutPostProps {
  sourceMediaInfo?: SourceMediaInfo | null
}

interface MediaTag {
  label: string
  icon: JSX.Element
}

const AI_GENERATED_IMAGE_TAG: MediaTag = {
  label: 'AI Generated Image',
  icon: <VisualStyle size={16} />,
}

const AI_GENERATED_VIDEO_TAG: MediaTag = {
  label: 'AI Generated Video',
  icon: <AIVideo size={16} />,
}

const STOCK_PHOTO_TAG: MediaTag = {
  label: 'Stock photo',
  icon: <Image03 size={16} />,
}

const STOCK_VIDEO_TAG: MediaTag = {
  label: 'Stock video',
  icon: <VideoOn size={16} />,
}

const BRAND_KIT_PHOTO_TAG: MediaTag = {
  label: 'Brand kit photo',
  icon: <Image03 size={16} />,
}

const BRAND_KIT_VIDEO_TAG: MediaTag = {
  label: 'Brand kit video',
  icon: <VideoOn size={16} />,
}

export const AboutPost = observer(({ sourceMediaInfo }: AboutPostProps) => {
  const { usageCosts } = useCreditUsage()
  const {
    tierUsageStore: { currentSubscription },
  } = useRootStore()
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer

  if (!sourceMediaInfo) return null

  const { provider, original_provider, mime_type } = sourceMediaInfo
  const isVideo = mime_type?.startsWith('video/')
  const isAIGeneratedImage = provider === 'ai_generated'
  const isAIGeneratedVideo = provider === 'veo3'
  const isCombinedAI = isAIGeneratedVideo && original_provider === 'ai_generated'

  const aiVideoCreditCost = usageCosts?.video_credits_cost ?? 0
  const aiImageCreditCost = usageCosts?.visual_styles_credits_cost ?? 0
  const contentCreditCost = usageCosts?.content_credits_cost ?? 0

  const tags: MediaTag[] = []
  let totalCredits = 0

  if (isCombinedAI) {
    tags.push(AI_GENERATED_IMAGE_TAG)
    tags.push(AI_GENERATED_VIDEO_TAG)
    totalCredits = aiImageCreditCost + aiVideoCreditCost
  } else if (isAIGeneratedImage) {
    tags.push(AI_GENERATED_IMAGE_TAG)
    totalCredits = aiImageCreditCost
  } else if (isAIGeneratedVideo) {
    tags.push(AI_GENERATED_VIDEO_TAG)
    totalCredits = aiVideoCreditCost
  } else {
    const isBrandKit = !provider
    if (isBrandKit) {
      tags.push(isVideo ? BRAND_KIT_VIDEO_TAG : BRAND_KIT_PHOTO_TAG)
    } else {
      tags.push(isVideo ? STOCK_VIDEO_TAG : STOCK_PHOTO_TAG)
    }
    totalCredits = isOnPioneer ? 0 : contentCreditCost
  }

  const creditsLabel = totalCredits === 1 ? 'credit' : 'credits'

  return (
    <ToolbarMenu title="About this post">
      <div className={Styles.container}>
        {tags.map((tag) => (
          <div key={tag.label} className={Styles.tag}>
            {tag.icon}
            <Text variant="secondary">{tag.label}</Text>
          </div>
        ))}
        {totalCredits > 0 && (
          <div className={Styles.tag}>
            <Credits size={16} />
            <Text variant="secondary">
              {totalCredits} {creditsLabel} used
            </Text>
          </div>
        )}
      </div>
    </ToolbarMenu>
  )
})
